import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://wsa-email-service-231048979843.us-east1.run.app/api/v1';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log('📤 Email API Request:', config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
apiClient.interceptors.response.use(
  (response) => {
    console.log('📥 Email API Response:', response.status);
    return response;
  },
  (error) => {
    console.error('❌ Email API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Converts form data to HTML email template
 * @param {Object} formData - The contact form data
 * @returns {string} HTML formatted email content
 */
const convertFormDataToHTML = (formData) => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background-color: #062e22 !important; color: #ffffff !important; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 8px 8px; }
        .section { margin-bottom: 25px; }
        .label { font-weight: bold; color: #555; display: inline-block; width: 120px; }
        .value { color: #333; }
        .message-section { background: white; padding: 20px; border-radius: 8px; border-left: 4px solid #062e22; }
        .footer { text-align: center; margin-top: 30px; color: #777; font-size: 12px; }
        .highlight { background: #f0f4f1; padding: 15px; border-radius: 6px; margin: 15px 0; border: 1px solid rgba(6,46,34,0.1); }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header" style="background-color: #062e22 !important; color: white !important; padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
          <h1 style="color: #ffffff !important; background-color: transparent !important; margin: 0 0 10px 0; font-size: 24px; font-family: Arial, sans-serif;">📸 New Photography Inquiry</h1>
          <p style="color: #ffffff !important; background-color: transparent !important; margin: 0; font-size: 16px; font-family: Arial, sans-serif;">Heather Walker Photography - Contact Form Submission</p>
        </div>
        
        <div class="content">
          <div class="highlight">
            <strong>📅 Received:</strong> ${currentDate}
          </div>
          
          <div class="section">
            <h3>👤 Customer Information</h3>
            <p><span class="label">Name:</span> <span class="value">${formData.name}</span></p>
            <p><span class="label">Email:</span> <span class="value"><a href="mailto:${formData.email}">${formData.email}</a></span></p>
            ${formData.phone ? `<p><span class="label">Phone:</span> <span class="value"><a href="tel:${formData.phone}">${formData.phone}</a></span></p>` : ''}
          </div>
          
          <div class="section">
            <h3>🎉 Event Details</h3>
            <p><span class="label">Event Type:</span> <span class="value">${formData.eventType}</span></p>
            ${formData.eventDate ? `<p><span class="label">Event Date:</span> <span class="value">${new Date(formData.eventDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></p>` : ''}
            ${formData.location ? `<p><span class="label">Location:</span> <span class="value">${formData.location}</span></p>` : ''}
          </div>
          
          ${formData.message ? `
          <div class="section">
            <h3>💬 Message</h3>
            <div class="message-section">
              ${formData.message.replace(/\n/g, '<br>')}
            </div>
          </div>
          ` : ''}
          
          <div class="footer">
            <p>This inquiry was submitted through the Heather Walker Photography website contact form.</p>
            <p>Please respond within 24 hours for the best customer experience.</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Email API Service - Handles form submission and email generation
 */
export const emailAPIService = {
  /**
   * Submits contact form data and sends email
   * @param {Object} formData - Contact form data
   * @param {string} formData.name - Customer name
   * @param {string} formData.email - Customer email
   * @param {string} formData.phone - Customer phone (optional)
   * @param {string} formData.eventType - Type of photography event
   * @param {string} formData.eventDate - Event date (optional)
   * @param {string} formData.location - Event location (optional)
   * @param {string} formData.message - Customer message (optional)
   * @returns {Promise<Object>} API response with success/error status
   */
  submitInquiry: async (formData) => {
    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.eventType) {
        throw new Error('Missing required fields: name, email, and event type are required');
      }

      // Convert form data to HTML
      const htmlContent = convertFormDataToHTML(formData);
      
      // Prepare payload for backend
      const payload = {
        // Original form data
        customerInfo: {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone?.trim() || null
        },
        eventDetails: {
          type: formData.eventType,
          date: formData.eventDate || null,
          location: formData.location?.trim() || null
        },
        message: formData.message?.trim() || null,
        
        // Email specific data
        emailData: {
          htmlContent: htmlContent,
          subject: `New ${formData.eventType} Inquiry from ${formData.name}`,
          recipientEmail: process.env.REACT_APP_BUSINESS_EMAIL || 'casey@walkerstudioarts.com, heather@walkerstudioarts.com',
          replyToEmail: formData.email
        },
        
        // Metadata
        metadata: {
          submittedAt: new Date().toISOString(),
          source: 'website-contact-form',
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct'
        }
      };

      console.log('Sending email inquiry payload:', payload);

      // Send to backend API
      const response = await apiClient.post('/wsaemailservice/sendemail', payload);
      
      return {
        success: true,
        data: response.data,
        message: 'Thank you for your inquiry! We\'ll get back to you within 24 hours.',
        emailSent: true
      };
      
    } catch (error) {
      console.error('Email API Service Error:', error);
      
      // Handle different error types
      if (error.message.includes('Missing required fields')) {
        return {
          success: false,
          error: error.message,
          type: 'validation'
        };
      }
      
      if (error.code === 'ECONNABORTED') {
        return {
          success: false,
          error: 'Request timed out. Please check your connection and try again.',
          type: 'timeout'
        };
      }
      
      if (!error.response) {
        return {
          success: false,
          error: 'Unable to connect to server. Please try again later.',
          type: 'network'
        };
      }
      
      const status = error.response.status;
      let errorMessage = 'Failed to send inquiry. Please try again.';
      
      if (status === 400) {
        errorMessage = error.response.data?.message || 'Invalid request. Please check your information.';
      } else if (status === 429) {
        errorMessage = 'Too many requests. Please wait a moment before trying again.';
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again in a few minutes.';
      }
      
      return {
        success: false,
        error: errorMessage,
        type: 'api',
        status: status
      };
    }
  },

  /**
   * Test connection to email API
   * @returns {Promise<Object>} Connection test result
   */
  testConnection: async () => {
    try {
      const response = await apiClient.get('/email/health');
      return {
        success: true,
        message: 'Email service is available',
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: 'Email service is unavailable',
        details: error.message
      };
    }
  }
};

export default emailAPIService;
